//HERBatchActions.jsx
import { useState } from "react";
import { useDispatch } from "react-redux";
import {
  Paper,
  Typography,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Box,
  Alert,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import {
  CheckCircle,
  Cancel,
  Warning,
  ExpandMore
} from "@mui/icons-material";
import { toast } from "react-toastify";
import { HER_ACTIONS } from "store/actions/herActions";
import "./HERStyles.scss";

const HERBatchActions = ({ batchId, endpointUrl, token }) => {
  const dispatch = useDispatch();
  const [selectedStatus, setSelectedStatus] = useState("");
  const [reason, setReason] = useState("");
  const [isUpdating, setIsUpdating] = useState(false);
  // Status-related state variables - commented out while status section is disabled
  // const [currentBatchStatus, setCurrentBatchStatus] = useState(null);
  // const [isLoadingStatus, setIsLoadingStatus] = useState(false);
  // const [statusError, setStatusError] = useState(null);
  const [expanded, setExpanded] = useState(false);

  const statusOptions = [
    { 
      value: "CSM_APPROVED", 
      label: "CSM Approved", 
      icon: <CheckCircle color="success" />,
      color: "success"
    },
    { 
      value: "CSM_REJECT", 
      label: "CSM Reject", 
      icon: <Cancel color="error" />,
      color: "error"
    },
    {
      value: "CSM_PARTIAL_REJECT",
      label: "CSM Partially Reject",
      icon: <Warning color="warning" />,
      color: "warning"
    },
  ];

  // Manual status fetching function - commented out while status section is disabled
  /*
  const fetchBatchStatus = async () => {
    if (!batchId || !endpointUrl || !token) return;

    setIsLoadingStatus(true);
    setStatusError(null);

    try {
      const url = `${endpointUrl}/2.1/utility_notifications/batch/${batchId}/status`;

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setCurrentBatchStatus(data);
    } catch (error) {
      console.error("Error fetching batch status:", error);
      setStatusError(error.message);
    } finally {
      setIsLoadingStatus(false);
    }
  };

  const handleRefreshStatus = () => {
    fetchBatchStatus();
  };
  */

  const handleStatusUpdate = async () => {
    if (!selectedStatus) {
      toast.error("Please select a status");
      return;
    }

    if (!reason.trim()) {
      toast.error("Please provide a reason");
      return;
    }

    setIsUpdating(true);

    try {
      // Use GET with query params instead of PUT with body
      const queryParams = new URLSearchParams({
        status: selectedStatus,
        reason: reason.trim(),
      });
      const url = `${endpointUrl}/2.1/utility_notifications/batch/${batchId}/status?${queryParams.toString()}`;

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      await response.json();
      toast.success(`Batch status updated to ${selectedStatus}`);

      // Remove the batch from Redux store after successful update
      dispatch(HER_ACTIONS.REMOVE_BATCH(batchId));

      // Reset form after successful update
      setSelectedStatus("");
      setReason("");

    } catch (error) {
      console.error("Error updating batch status:", error);
      toast.error(`Failed to update batch status: ${error.message}`);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleReset = () => {
    setSelectedStatus("");
    setReason("");
  };

  // Status chip color function - commented out while status section is disabled
  /*
  const getStatusChipColor = (status) => {
    if (!status) return 'default';
    switch (status.toLowerCase()) {
      case 'csm_approved':
        return 'success';
      case 'csm_reject':
        return 'error';
      case 'csm_partially_reject':
        return 'warning';
      default:
        return 'info';
    }
  };
  */

  return (
    <Paper elevation={2} sx={{ mt: 2, mb: 2 }} className="her-batch-actions">
      {/* Current Status Section - COMMENTED OUT FOR NOW */}
      {/*
      <Accordion
        expanded={true}
        sx={{ boxShadow: 'none', '&:before': { display: 'none' } }}
        className="status-section"
      >
        <AccordionSummary sx={{ cursor: 'default !important' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
            <Info color="primary" />
            <Typography variant="h6">
              Batch Status: <strong>{batchId}</strong>
            </Typography>
            <Button
              size="small"
              onClick={handleRefreshStatus}
              disabled={isLoadingStatus}
              startIcon={isLoadingStatus ? <CircularProgress size={16} /> : <Refresh />}
              sx={{ ml: 'auto' }}
            >
              Refresh
            </Button>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          {statusError ? (
            <Alert severity="error">
              Failed to load batch status: {statusError}
            </Alert>
          ) : isLoadingStatus ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <CircularProgress size={20} />
              <Typography>Loading batch status...</Typography>
            </Box>
          ) : currentBatchStatus ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography variant="body1">Current Status:</Typography>
                <Chip
                  label={currentBatchStatus.status || 'Unknown'}
                  color={getStatusChipColor(currentBatchStatus.status)}
                  size="small"
                />
              </Box>
              {currentBatchStatus.reason && (
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Reason: {currentBatchStatus.reason}
                  </Typography>
                </Box>
              )}
              {currentBatchStatus.updatedAt && (
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Last Updated: {new Date(currentBatchStatus.updatedAt).toLocaleString()}
                  </Typography>
                </Box>
              )}
            </Box>
          ) : (
            <Box sx={{ textAlign: 'center', py: 2 }}>
              <Typography color="text.secondary" gutterBottom>
                Click "Refresh" to load batch status
              </Typography>
              <Button
                variant="outlined"
                size="small"
                onClick={handleRefreshStatus}
                startIcon={<Refresh />}
              >
                Load Status
              </Button>
            </Box>
          )}
        </AccordionDetails>
      </Accordion>

      <Divider />
      */}

      {/* Batch Actions Section */}
      <Accordion
        expanded={expanded}
        onChange={(_, isExpanded) => setExpanded(isExpanded)}
        className="actions-section"
      >
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Typography variant="h6">Batch Actions</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Alert severity="info" sx={{ mb: 2 }}>
            Update the status for the entire batch: <strong>{batchId}</strong>
          </Alert>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <FormControl fullWidth>
              <InputLabel>Select Status*</InputLabel>
              <Select
                value={selectedStatus}
                onChange={(e) => setSelectedStatus(e.target.value)}
                label="Select Status*"
                disabled={isUpdating}
              >
                {statusOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {option.icon}
                      {option.label}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              label="Reason*"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              variant="outlined"
              fullWidth
              multiline
              rows={3}
              required
              disabled={isUpdating}
              placeholder="Provide a reason for this status change..."
              helperText="Explain why you're updating the batch status"
            />

            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button
                variant="contained"
                color="primary"
                onClick={handleStatusUpdate}
                disabled={!selectedStatus || !reason.trim() || isUpdating}
                startIcon={isUpdating ? <CircularProgress size={20} /> : null}
              >
                {isUpdating ? "Updating..." : "Update Batch Status"}
              </Button>

              <Button
                variant="outlined"
                onClick={handleReset}
                disabled={isUpdating}
              >
                Reset
              </Button>
            </Box>
          </Box>
        </AccordionDetails>
      </Accordion>
    </Paper>
  );
};

export default HERBatchActions;
